/**
 * MeetingInterface.tsx
 * 
 * Full-page meeting interface using Modern MediaSFU UI.
 * Uses glassmorphic design with ModernMediasfuGenericLimited (includes MiniTour).
 */

import React, { useMemo } from 'react';
import { ModernMediasfuGenericLimited, ModernMeetingSetupWrapper } from 'mediasfu-reactjs';
import 'mediasfu-reactjs/dist/main.css';
import { Helmet } from 'react-helmet-async';
import Cookies from 'js-cookie';

/**
 * Credentials interface for MediaSFU API
 */
interface Credentials {
  apiUserName: string;
  apiKey: string;
}

/**
 * MeetingInterface - Modern MediaSFU meeting component
 */
const MeetingInterface: React.FC = () => {
  // API credentials from cookies or empty for demo
  const credentials: Credentials = useMemo(() => ({
    apiUserName: Cookies.get('apiUserName') || '',
    apiKey: Cookies.get('apiKey') || '',
  }), []);
  // Connect to MediaSFU Cloud
  const connectMediaSFU = true;

  // Compute personal translation eligibility from systemCreditsData cookie
  const { canUsePersonalTranslation, personalTranslationUsername } = useMemo(() => {
    try {
      const username = Cookies.get('username') || '';
      if (!username) {
        return { canUsePersonalTranslation: false, personalTranslationUsername: undefined };
      }

      const creditsRaw = Cookies.get('systemCreditsData');
      if (!creditsRaw) {
        return { canUsePersonalTranslation: false, personalTranslationUsername: undefined };
      }

      const systemCredits = typeof creditsRaw === 'string' ? JSON.parse(creditsRaw) : creditsRaw;

      const useSystemTranslation = systemCredits?.preferences?.useSystemTranslationConfigs ?? false;
      const translationBalance = systemCredits?.translationCredits?.balance ?? 0;

      if (useSystemTranslation && translationBalance > 0) {
        return { canUsePersonalTranslation: true, personalTranslationUsername: username };
      }

      return { canUsePersonalTranslation: false, personalTranslationUsername: undefined };
    } catch {
      return { canUsePersonalTranslation: false, personalTranslationUsername: undefined };
    }
  }, []);
  
  return (
    <>
      <Helmet>
        <title>MediaSFU Meeting - Speak & Listen in Any Language | AI Video Conferencing</title>
        <meta name="title" content="MediaSFU Meeting - Speak & Listen in Any Language | AI Video Conferencing" />
        <meta name="description" content="Join or host a MediaSFU meeting with AI agents, real-time translation (speak and listen in any language), cloud recording, screen sharing, and collaboration features." />
        <meta name="keywords" content="video conferencing, meeting, MediaSFU, AI agents, real-time translation, multi-language support, speak any language, listen any language, cloud recording, screen sharing, webinar" />
        <meta name="author" content="MediaSFU" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://meeting.mediasfu.com/" />
        <meta property="og:title" content="MediaSFU Meeting - Speak & Listen in Any Language" />
        <meta property="og:description" content="Join or host a MediaSFU meeting with AI agents, real-time translation (speak and listen in any language), cloud recording, and screen sharing." />
        <meta property="og:image" content="/logoPortrait.png" />
        <meta property="og:site_name" content="MediaSFU Meeting" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://meeting.mediasfu.com/" />
        <meta name="twitter:title" content="MediaSFU Meeting - Speak & Listen in Any Language" />
        <meta name="twitter:description" content="AI-powered video conferencing with real-time translation - speak and listen in any language." />
        <meta name="twitter:image" content="/logo1000.png" />
        <meta name="twitter:creator" content="@mediasfu" />
        
        {/* Apple/Mobile Meta Tags */}
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        <meta name="apple-mobile-web-app-title" content="MediaSFU Meeting" />
        <meta name="theme-color" content="#0f172a" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://meeting.mediasfu.com/" />
        
        {/* Structured Data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "MediaSFU Meeting",
            "description": "AI-powered video conferencing platform",
            "url": "https://meeting.mediasfu.com",
            "applicationCategory": "CommunicationApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "author": {
              "@type": "Organization",
              "name": "MediaSFU",
              "url": "https://mediasfu.com"
            }
          })}
        </script>
      </Helmet>
      
       {/* Render directly without wrapper - matches working example */}

      <ModernMediasfuGenericLimited
        PrejoinPage={(options) => (
          <ModernMeetingSetupWrapper 
            {...options}
          />
        )}
        credentials={credentials}
        connectMediaSFU={connectMediaSFU}
        canUsePersonalTranslation={canUsePersonalTranslation}
        personalTranslationUsername={personalTranslationUsername}
      />
    </>
  );
};

export default MeetingInterface;
